// Kampányok (UI-2): every campaign, newest first, by status.
//
// One row is a campaign as a marketer means it — the group over its
// countries — with the countries' codes, the state, the planned local send
// time, the recipients once assembled, and, once something went out, the open,
// click and unsubscribe figures the reports give (a dash where they give none;
// nothing here computes an engagement number of its own).
//
// Numbers from `GET /campaign-groups/overview` (two queries for the whole
// page) and `GET /reports/campaigns` (once per load, from the earliest start
// on the page, not on every refresh — it asks the event warehouse).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PlusIcon, SearchIcon } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { cancelCampaignGroup, fetchCampaignGroupOverview, fetchReportList } from "@/lib/api";
import type { CampaignGroupOverview, CampaignGroupRow, ReportRow } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ActionDialog } from "@/components/emc/ActionDialog";
import { DataTable, TablePager } from "@/components/emc/DataTable";
import { PageHeader } from "@/components/emc/PageHeader";
import { RowActions } from "@/components/emc/RowActions";
import type { RowAction } from "@/components/emc/RowActions";
import { StatusPill } from "@/components/emc/StatusPill";
import { Toolbar } from "@/components/emc/Toolbar";
import { EmptyState, ErrorState } from "@/components/emc/states";
import { count, share } from "@/components/emc/format";
import { campaignGroupShown, labelOf } from "@/components/emc/status";
import { assertMoved, pauseAll, pausedOf, resumeAll, sendingOf } from "@/pages/campaigns/actions";
import {
  COMPOSING,
  MOVING,
  TABS,
  assembled,
  engagementOf,
  messageOf,
  showLocal,
  startsLine,
  totalsOf,
} from "@/pages/campaigns/model";
import { useSendingIdentities } from "@/pages/campaigns/data";
import { CountryCodes } from "@/pages/campaigns/parts";

const PAGE = 50;
// The overview endpoint's own ceiling on `limit`.
const MAX_PAGE = 200;
const REFRESH_MS = 15_000;
const SEARCH_DELAY_MS = 300;

export type Pending =
  | { kind: "cancel"; row: CampaignGroupRow }
  | { kind: "pause"; row: CampaignGroupRow }
  | { kind: "resume"; row: CampaignGroupRow };

export function hrefOf(row: { id: number; state: CampaignGroupRow["state"] }): string {
  return COMPOSING.has(row.state) ? `/kampanyok/${row.id}/szerkesztes` : `/kampanyok/${row.id}`;
}

export default function CampaignList() {
  const navigate = useNavigate();
  const { identityOf } = useSendingIdentities();
  const zoneOf = (country: string) => identityOf(country)?.timezone;
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((entry) => entry.key === params.get("allapot")) ?? TABS[0]!;
  const q = params.get("q") ?? "";
  const [typed, setTyped] = useState(q);
  const [data, setData] = useState<CampaignGroupOverview | null>(null);
  const [extra, setExtra] = useState<CampaignGroupRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reports, setReports] = useState<Map<number, ReportRow>>(new Map());
  const [reportsFailed, setReportsFailed] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const request = useRef(0);
  // How many rows are on screen, so a quiet refresh brings back as many as
  // "Továbbiak" had loaded instead of collapsing the list to one page.
  const shown = useRef(PAGE);

  const load = useCallback(
    async (quiet = false) => {
      request.current += 1;
      const ticket = request.current;
      if (!quiet) {
        setLoading(true);
        shown.current = PAGE;
      }
      try {
        const answer = await fetchCampaignGroupOverview({
          q,
          states: tab.states,
          limit: Math.min(MAX_PAGE, Math.max(PAGE, shown.current)),
        });
        if (ticket !== request.current) return;
        setData(answer);
        setExtra([]);
        setError(null);
      } catch (raised: unknown) {
        if (ticket === request.current) setError(messageOf(raised));
      } finally {
        if (ticket === request.current) setLoading(false);
      }
    },
    [q, tab.states],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // The search waits for the typing to stop.
  useEffect(() => {
    if (typed === q) return undefined;
    const timer = window.setTimeout(() => {
      const next = new URLSearchParams(params);
      if (typed.trim()) next.set("q", typed.trim());
      else next.delete("q");
      setParams(next, { replace: true });
    }, SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [typed, q, params, setParams]);

  const rows = useMemo(() => [...(data?.items ?? []), ...extra], [data, extra]);

  // While anything is moving, the numbers follow it.
  useEffect(() => {
    if (!rows.some((row) => MOVING.has(row.state))) return undefined;
    const timer = window.setInterval(() => void load(true), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [rows, load]);

  // The report figures, for the rows that have sent something.
  const since = useMemo(() => {
    const starts = rows.flatMap((row) => row.countries.map((country) => country.started_at)).filter(
      (value): value is string => value !== null,
    );
    return starts.length === 0 ? null : starts.reduce((a, b) => (a < b ? a : b)).slice(0, 10);
  }, [rows]);
  useEffect(() => {
    if (since === null) return;
    fetchReportList({ since, source: "own" })
      .then((answer) => {
        setReports(
          new Map(
            answer.rows
              .filter((row): row is ReportRow & { campaign_id: number } => row.campaign_id !== null)
              .map((row) => [row.campaign_id, row] as const),
          ),
        );
        setReportsFailed(false);
      })
      .catch(() => setReportsFailed(true));
  }, [since]);

  async function more() {
    try {
      const answer = await fetchCampaignGroupOverview({
        q,
        states: tab.states,
        limit: PAGE,
        offset: rows.length,
      });
      setExtra((current) => [...current, ...answer.items]);
      setData((current) => (current === null ? answer : { ...current, has_more: answer.has_more }));
      shown.current = rows.length + answer.items.length;
    } catch (raised: unknown) {
      setError(messageOf(raised));
    }
  }

  function tabCount(states: string[]): number | null {
    if (data === null) return null;
    const counts = data.counts as Record<string, number>;
    const keys = states.length === 0 ? Object.keys(counts) : states;
    return keys.reduce((sum, key) => sum + (counts[key] ?? 0), 0);
  }

  const columns: ColumnDef<CampaignGroupRow>[] = [
    {
      id: "name",
      header: "Név",
      cell: ({ row }) => {
        const failed = row.original.countries.filter((country) => country.state === "failed");
        return (
          <span className="flex flex-col">
            <Link
              to={hrefOf(row.original)}
              className="font-medium text-ink hover:text-blue-ink"
              onClick={(event) => event.stopPropagation()}
            >
              {row.original.name}
            </Link>
            <span className="text-label text-muted-foreground">
              {labelOf("campaignMode", row.original.mode)}
              {row.original.ab !== null ? " · A/B-teszt" : ""}
            </span>
            {failed.length > 0 && (
              <span className="text-label text-danger">
                hibás ország: {failed.map((country) => country.country).join(", ")}
              </span>
            )}
          </span>
        );
      },
      meta: { wrap: true },
    },
    {
      id: "countries",
      header: "Országok",
      cell: ({ row }) => <CountryCodes countries={row.original.countries} />,
      meta: { wrap: true },
    },
    {
      id: "state",
      header: "Állapot",
      cell: ({ row }) => <StatusPill domain="campaignGroup" state={campaignGroupShown(row.original)} title={row.original.state} />,
    },
    {
      id: "when",
      header: "Küldés",
      cell: ({ row }) => {
        // The reader's clock first, the countries' own in brackets.
        const line = startsLine(
          row.original.countries.map((country) => ({ country: country.country, at: country.send_at })),
          zoneOf,
        );
        if (line === null) {
          return row.original.scheduled_local ? (
            <span className="flex flex-col">
              <span className="data-mono">{showLocal(row.original.scheduled_local)}</span>
              <span className="text-label text-muted-foreground">helyi idő</span>
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        }
        return (
          <span className="flex flex-col">
            <span className="data-mono">{line.viewer}</span>
            {line.local !== null && <span className="text-label text-muted-foreground">({line.local})</span>}
          </span>
        );
      },
      meta: { wrap: true },
    },
    {
      id: "recipients",
      header: "Címzett",
      cell: ({ row }) => {
        if (!assembled(row.original.countries)) return <span className="text-muted-foreground">—</span>;
        const totals = totalsOf(row.original.countries);
        return (
          <span className="flex flex-col items-end">
            <span>{count(totals.recipients)}</span>
            {MOVING.has(row.original.state) && (
              <span className="text-label text-muted-foreground">
                elküldve {share(totals.sent, totals.recipients) ?? "0%"}
              </span>
            )}
          </span>
        );
      },
      meta: { align: "right" },
    },
    {
      id: "opens",
      header: "Nyitás",
      cell: ({ row }) => {
        const found = engagementOf(row.original.countries, reports);
        // Every open over the delivered, the Klaviyo way (owner, 2026-10-24).
        return found === null ? "—" : (share(found.opens, found.delivered) ?? "—");
      },
      meta: { align: "right" },
    },
    {
      id: "clicks",
      header: "Kattintás",
      cell: ({ row }) => {
        const found = engagementOf(row.original.countries, reports);
        return found === null ? "—" : (share(found.clicks, found.sent) ?? "—");
      },
      meta: { align: "right" },
    },
    {
      id: "unsubscribed",
      header: "Leiratkozás",
      cell: ({ row }) => {
        const found = engagementOf(row.original.countries, reports);
        if (found === null || found.unsubscribed === null) return "—";
        return (
          <span className="flex flex-col items-end">
            <span>{count(found.unsubscribed)}</span>
            <span className="text-label text-muted-foreground">{share(found.unsubscribed, found.sent) ?? "—"}</span>
          </span>
        );
      },
      meta: { align: "right" },
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Műveletek</span>,
      cell: ({ row }) => {
        const group = row.original;
        const actions: RowAction[] = [
          { label: "Megnyitás", onSelect: () => navigate(`/kampanyok/${group.id}`) },
        ];
        if (COMPOSING.has(group.state)) {
          actions.push({ label: "Szerkesztés", onSelect: () => navigate(`/kampanyok/${group.id}/szerkesztes`) });
        }
        if (sendingOf(group.countries).length > 0) {
          actions.push({ label: "Szüneteltetés", onSelect: () => setPending({ kind: "pause", row: group }) });
        }
        if (pausedOf(group.countries).length > 0) {
          actions.push({ label: "Folytatás", onSelect: () => setPending({ kind: "resume", row: group }) });
        }
        if (COMPOSING.has(group.state) || MOVING.has(group.state)) {
          actions.push({
            label: COMPOSING.has(group.state) ? "Lemondás" : "Leállítás",
            danger: true,
            onSelect: () => setPending({ kind: "cancel", row: group }),
          });
        }
        return <RowActions actions={actions} label={`${group.name}: műveletek`} />;
      },
      meta: { align: "right" },
    },
  ];

  const noun = tab.key === "osszes" ? "kampány" : `„${tab.label.toLowerCase()}” kampány`;

  return (
    <>
      <PageHeader
        title="Kampányok"
        description="Egy kampány egyszer készül, és minden célországba a saját listájával, feladójával és nyelvén megy ki."
        actions={
          <Button asChild>
            <Link to="/kampanyok/uj">
              <PlusIcon />
              Új kampány
            </Link>
          </Button>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Tabs
          value={tab.key}
          onValueChange={(value) => {
            const next = new URLSearchParams(params);
            if (value === TABS[0]!.key) next.delete("allapot");
            else next.set("allapot", value);
            setParams(next);
          }}
        >
          <TabsList aria-label="Állapot szerint">
            {TABS.map((entry) => {
              const number = tabCount(entry.states);
              return (
                <TabsTrigger key={entry.key} value={entry.key}>
                  {entry.label}
                  {number !== null && <span className="text-label text-muted-foreground">{count(number)}</span>}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>

        <Toolbar>
          <div className="relative">
            <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-label="Keresés név szerint"
              placeholder="Keresés név szerint"
              className="w-72 border-transparent bg-fill-secondary pl-8 dark:bg-fill-secondary"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>
        </Toolbar>
      </div>

      {error !== null && <ErrorState message={error} className="mb-4" onRetry={() => void load()} />}
      {reportsFailed && (
        <p className="mb-3 text-label text-muted-foreground">
          A nyitás, kattintás és leiratkozás számai most nem érhetők el (a riport nem válaszolt) — ezért áll ott „—”.
        </p>
      )}

      <DataTable
        label="Kampányok"
        columns={columns}
        data={rows}
        loading={loading && data === null}
        getRowId={(row) => String(row.id)}
        onRowClick={(row) => navigate(hrefOf(row))}
        empty={
          <EmptyState
            bare
            title={q ? `Nincs „${q}” nevű ${noun}.` : `Nincs ${noun}.`}
            description={
              q
                ? "Más szó, vagy egy másik fül?"
                : "Egy kampány egyszer készül, és minden kiválasztott országba kimegy."
            }
            action={
              !q && (tab.key === "osszes" || tab.key === "vazlat") ? (
                <Button asChild>
                  <Link to="/kampanyok/uj">
                    <PlusIcon />
                    Új kampány
                  </Link>
                </Button>
              ) : undefined
            }
          />
        }
        footer={
          rows.length > 0 ? (
            <TablePager summary={`${count(rows.length)} kampány${data?.has_more ? "" : " · ennyi van"}`}>
              {data?.has_more && (
                <Button variant="outline" type="button" onClick={() => void more()}>
                  Továbbiak
                </Button>
              )}
            </TablePager>
          ) : undefined
        }
      />

      <GroupActionDialog pending={pending} onClose={() => setPending(null)} onDone={() => void load(true)} />
    </>
  );
}

/** Pause, resume or stop a whole campaign — the list's row menu and the
 * detail page's header share it. */
export function GroupActionDialog({
  pending,
  onClose,
  onDone,
}: {
  pending: Pending | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const row = pending?.row;
  const composing = row !== undefined && COMPOSING.has(row.state);
  const sending = row === undefined ? [] : sendingOf(row.countries);
  const paused = row === undefined ? [] : pausedOf(row.countries);

  return (
    <>
      <ActionDialog
        open={pending?.kind === "pause"}
        onOpenChange={(open) => !open && onClose()}
        title="A kiküldés szüneteltetése"
        description={
          <>
            Megáll a kiküldés ezekben az országokban:{" "}
            <span className="data-mono">{sending.map((country) => country.country).join(", ")}</span>. A
            már kiküldött levél nem vonható vissza; a hátralévő címzettek a folytatásig várnak.
          </>
        }
        confirmLabel="Szüneteltetés"
        onConfirm={async () => {
          if (row === undefined) return;
          const result = assertMoved(await pauseAll(row.countries));
          toast.success(`A kiküldés szünetel: ${result.done.join(", ")}`, {
            description: result.refused.length ? `Nem állt meg: ${result.refused.map((r) => `${r.country} (${r.message})`).join(", ")}` : undefined,
          });
          onDone();
        }}
      />
      <ActionDialog
        open={pending?.kind === "resume"}
        onOpenChange={(open) => !open && onClose()}
        title="A kiküldés folytatása"
        description={
          <>
            Újraindul a kiküldés ezekben az országokban:{" "}
            <span className="data-mono">{paused.map((country) => country.country).join(", ")}</span>. A
            warmup-keret és a küldési ablak továbbra is érvényes.
          </>
        }
        confirmLabel="Folytatás"
        onConfirm={async () => {
          if (row === undefined) return;
          const result = assertMoved(await resumeAll(row.countries));
          toast.success(`A kiküldés folytatódik: ${result.done.join(", ")}`, {
            description: result.refused.length ? `Nem indult újra: ${result.refused.map((r) => `${r.country} (${r.message})`).join(", ")}` : undefined,
          });
          onDone();
        }}
      />
      <ActionDialog
        open={pending?.kind === "cancel"}
        onOpenChange={(open) => !open && onClose()}
        tone="danger"
        title={composing ? "A kampány lemondása" : "A kampány leállítása"}
        description={
          composing
            ? "A vázlat lemondott állapotba kerül, és nem küldhető ki. Ez nem vonható vissza."
            : "A kampány minden még futó országa leáll: a hátralévő címzettek nem kapnak levelet. A már kiküldött levél nem vonható vissza, és a leállítás sem."
        }
        reason={{ label: "Miért?", placeholder: "pl. rossz lista került bele" }}
        confirmLabel={composing ? "Lemondás" : "Leállítás"}
        onConfirm={async (reason) => {
          if (row === undefined) return;
          await cancelCampaignGroup(row.id, reason);
          toast.success(composing ? "A kampány lemondva" : "A kampány leállt", { description: row.name });
          onDone();
        }}
      />
    </>
  );
}
